import React, { useRef } from 'react';
import Icon from '../../../components/AppIcon';
import UploadedImageThumb from '../../../components/UploadedImageThumb';
import toast from 'react-hot-toast';
import { isGeneratedImageDrop, getGeneratedImageUrl } from '../../../utils/dragDropUtils';

const MoodImageUpload = ({
  moodImages,
  setMoodImages,
  isDragOver,
  setIsDragOver,
  maxRemaining = 6,
  userId,
  storageService
}) => {
  const moodFileInputRef = useRef(null);
  const isUploadDisabled = maxRemaining <= 0;

  const handleFileUpload = async (files) => {
    if (isUploadDisabled) {
      toast.error('Maximum 6 total images reached across all uploads');
      return;
    }

    const validFiles = Array.from(files).filter(file => {
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
      const maxSize = 10 * 1024 * 1024; // 10MB

      if (!validTypes.includes(file.type)) {
        toast.error(`${file.name} is not a valid image format (PNG/JPG only)`);
        return false;
      }

      if (file.size > maxSize) {
        toast.error(`${file.name} is too large (max 10MB)`);
        return false;
      }

      return true;
    });

    const limitedFiles = validFiles.slice(0, maxRemaining);
    if (limitedFiles.length < validFiles.length) {
      toast.error(`Only ${maxRemaining} more image(s) allowed (6 total max)`);
    }

    if (limitedFiles.length === 0) return;

    const newImageUrls = (await Promise.all(limitedFiles.map(file => storageService.uploadFile(file, userId)))).filter(Boolean);
    setMoodImages(prevImages => [...prevImages, ...newImageUrls]);
  };

  const removeImage = (index) => {
    const newImages = moodImages.filter((_, i) => i !== index);
    setMoodImages(newImages);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (isGeneratedImageDrop(e)) {
      const url = getGeneratedImageUrl(e);
      if (url) {
        if (isUploadDisabled) {
          toast.error('Maximum 6 total images reached across all uploads');
          return;
        }
        if (maxRemaining <= 0) {
          toast.error(`Only ${maxRemaining} more image(s) allowed (6 total max)`);
          return;
        }
        setMoodImages(prev => [...prev, url]);
      }
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  };

  return (
    <div className="glassmorphic-card rounded-2xl p-5">
      <h3 className="text-white text-sm font-medium mb-3.5">
        Mood image(s)<span className="text-red-500">*</span>
      </h3>
      <label className={`block ${isUploadDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
        <input
          ref={moodFileInputRef}
          type="file"
          multiple
          accept="image/png,image/jpeg,image/jpg"
          onChange={(e) => handleFileUpload(e.target.files)}
          className="hidden"
          disabled={isUploadDisabled}
        />
        <div
          onDragOver={handleDragOver}
          onDragEnter={!isUploadDisabled ? handleDragEnter : undefined}
          onDragLeave={handleDragLeave}
          onDrop={!isUploadDisabled ? handleDrop : (e) => { e.preventDefault(); e.stopPropagation(); }}
          className={`glassmorphic-upload-zone rounded-2xl p-7 text-center border-2 border-dashed transition-all duration-300 ${
            isUploadDisabled
              ? 'border-white/10 bg-white/5'
              : isDragOver
                ? 'border-orange-500 bg-orange-500/10'
                : 'border-white/20 hover:border-orange-500/40'
          }`}
        >
          <Icon
            name={isDragOver && !isUploadDisabled ? "Upload" : "Image"}
            size={36}
            className={`mx-auto mb-2.5 transition-colors ${
              isUploadDisabled ? "text-white/30" : isDragOver ? "text-orange-400" : "text-orange-500"
            }`}
          />
          <p className={`text-sm transition-colors ${
            isUploadDisabled ? "text-white/30" : isDragOver ? "text-white font-medium" : "text-white/70"
          }`}>
            {isUploadDisabled
              ? "Upload limit reached (6 total)"
              : isDragOver
                ? "Drop mood images here"
                : "Upload image PNG or JPG"}
          </p>
        </div>
      </label>
      {moodImages.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {moodImages.map((image, index) => (
            <UploadedImageThumb
              key={index}
              src={image}
              alt={`Mood ${index + 1}`}
              onRemove={() => removeImage(index)}
              imgClass="max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10"
              height="h-auto"
              previewWidth="w-[200px]"
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MoodImageUpload;